import { useState } from 'react'
import { usePlaybooks, usePlaybook, useSavePlaybook } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'

interface Props {
  tenantId: string | null
}

const editorStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 420,
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--color-text)',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  padding: 16,
  resize: 'vertical',
  lineHeight: 1.6,
}

const NEW_FILE_PLACEHOLDER = `---
# New bootstrap playbook
- name: Bootstrap keel device
  hosts: all
  become: true
  tasks:
    - name: Ping
      ping:
`

export function Playbooks({ tenantId }: Props) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [editedContent, setEditedContent] = useState<string | null>(null)
  const [commitMessage, setCommitMessage] = useState('')
  const [newFilename, setNewFilename] = useState('')
  const [showNewFile, setShowNewFile] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data: files, isLoading: filesLoading, error: filesError } = usePlaybooks(tenantId)
  const { data: file, isLoading: fileLoading } = usePlaybook(
    tenantId,
    selectedFile && !showNewFile ? selectedFile : null,
  )
  const savePlaybook = useSavePlaybook(tenantId ?? '')

  // When file data arrives, initialise the editor.
  const displayContent = editedContent ?? (
    showNewFile ? NEW_FILE_PLACEHOLDER : (file?.content ?? '')
  )

  function handleSelectFile(name: string) {
    setSelectedFile(name)
    setEditedContent(null)
    setCommitMessage('')
    setShowNewFile(false)
  }

  function handleNewFile() {
    setSelectedFile(null)
    setEditedContent(NEW_FILE_PLACEHOLDER)
    setNewFilename('')
    setCommitMessage('')
    setShowNewFile(true)
  }

  async function handleSave() {
    if (!tenantId) return
    const filename = showNewFile ? newFilename.trim() : selectedFile
    if (!filename) return
    const content = editedContent ?? file?.content ?? ''
    if (!content.trim()) return

    await savePlaybook.mutateAsync({
      filename,
      content,
      message: commitMessage.trim() || undefined,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    if (showNewFile) {
      setSelectedFile(filename)
      setShowNewFile(false)
    }
    setEditedContent(null)
    setCommitMessage('')
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage playbooks.</div>
  }

  const fileList = files ?? []
  const hasChanges = editedContent !== null && editedContent !== (file?.content ?? '')

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Playbooks</h1>
          <p className="page-subtitle">Ansible playbooks stored in Gitea — select to view or edit</p>
        </div>
        <RequirePermission perm="fleets:write">
          <button className="btn btn-primary" onClick={handleNewFile}>
            + New playbook
          </button>
        </RequirePermission>
      </div>

      {filesError && (
        <div
          style={{
            background: 'rgba(220,38,38,0.1)',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 20,
          }}
        >
          Could not load playbooks. Make sure tenant onboarding is complete and Gitea is reachable.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20, alignItems: 'start' }}>
        {/* File list */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
          }}
        >
          {filesLoading ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--color-text-muted)' }}>Loading…</div>
          ) : fileList.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--color-text-muted)' }}>
              No playbooks yet.
            </div>
          ) : (
            fileList.map(f => (
              <button
                key={f.name}
                onClick={() => handleSelectFile(f.name)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 14px',
                  textAlign: 'left',
                  background: selectedFile === f.name && !showNewFile
                    ? 'var(--color-primary)'
                    : 'transparent',
                  color: selectedFile === f.name && !showNewFile
                    ? '#fff'
                    : 'var(--color-text)',
                  border: 'none',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {f.name}
              </button>
            ))
          )}
        </div>

        {/* Editor panel */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
          }}
        >
          {!selectedFile && !showNewFile ? (
            <div className="empty-state" style={{ minHeight: 200 }}>
              Select a playbook to edit, or create a new one.
            </div>
          ) : (
            <>
              {/* Filename header */}
              <div style={{ marginBottom: 12 }}>
                {showNewFile ? (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                      Filename <span style={{ color: 'var(--color-danger)' }}>*</span>
                    </label>
                    <input
                      value={newFilename}
                      onChange={e => setNewFilename(e.target.value)}
                      placeholder="e.g. my-bootstrap.yml"
                      style={{
                        background: 'var(--color-surface-2)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text)',
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 13,
                        fontFamily: 'var(--font-mono)',
                        width: 280,
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600 }}>
                    {selectedFile}
                  </div>
                )}
              </div>

              {/* Content editor */}
              {fileLoading ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '20px 0' }}>Loading file…</div>
              ) : (
                <textarea
                  value={displayContent}
                  onChange={e => setEditedContent(e.target.value)}
                  style={editorStyle}
                  spellCheck={false}
                />
              )}

              {/* Save controls */}
              <RequirePermission perm="fleets:write">
                <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={commitMessage}
                    onChange={e => setCommitMessage(e.target.value)}
                    placeholder="Commit message (optional)"
                    style={{
                      background: 'var(--color-surface-2)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)',
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12,
                      flex: 1,
                      minWidth: 200,
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={
                      savePlaybook.isPending ||
                      (showNewFile && !newFilename.trim()) ||
                      (!hasChanges && !showNewFile)
                    }
                  >
                    {savePlaybook.isPending ? 'Saving…' : saved ? 'Saved ✓' : 'Save & commit'}
                  </button>
                  {(hasChanges || showNewFile) && (
                    <button
                      className="btn btn-ghost"
                      onClick={() => {
                        setEditedContent(null)
                        setShowNewFile(false)
                        setNewFilename('')
                      }}
                    >
                      Discard
                    </button>
                  )}
                </div>
                {savePlaybook.isError && (
                  <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 8 }}>
                    Save failed. Check console for details.
                  </div>
                )}
              </RequirePermission>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
